import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import envProperties from "../../config/envProperties";

// CRITICAL FIX: Create axios instance with Authorization header interceptor
const apiUrl = process.env.REACT_APP_API_URL;
const adminAxios = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },

});

// Request interceptor to add JWT token to all admin API calls
adminAxios.interceptors.request.use(
  (config) => {

    // For MSIL users, use msil_id_token (contains email claims)
    // For MASS users, use sessionStorage token
    const token = localStorage.getItem('msil_id_token') || sessionStorage.getItem('token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


const getFiscalYear = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0 = Jan, 11 = Dec
  const startYear = month < 3 ? year - 1 : year; // Jan–Mar belong to previous FY
  const endYear = startYear + 1;
  return `FY ${String(startYear).slice(2)}-${String(endYear).slice(2)}`;
};

const BusinessInsightsChart = () => {
  const { t } = useTranslation();

  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);


  const revenueUrl =
    envProperties?.msil?.revenueUrl ||
    "https://massbusinesszone.com/api/v1/admin/revenue";

  // 🔹 Fetch revenue data from backend
  useEffect(() => {
    const fetchRevenue = async () => {
      try {
        // VAPT FIX: Use generic admin data endpoint to hide internal API structure
        // Extract orderDate from revenueUrl if present, otherwise use null
        let orderDate = null;
        if (revenueUrl) {
          try {
            const urlObj = new URL(revenueUrl);
            const params = new URLSearchParams(urlObj.search);
            orderDate = params.get('orderDate');
          } catch (e) {
            // If revenueUrl is not a valid URL, ignore
          }
        }
        
        const requestBody = { type: "revenue" };
        if (orderDate) {
          requestBody.orderDate = orderDate;
        }
        
        const res = await adminAxios.post('/api/v1/admin/data', requestBody);

        setRevenueData(res.data.data);
      } catch (err) {
        console.error("Error fetching revenue data:", err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          // Authentication/authorization error - redirect handled by interceptor
          setLoading(false);
          return;
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRevenue();
  }, []);

  // 🔹 Group by month
  const monthlyData = useMemo(() => {
    const monthlyTotals = {};

    (revenueData || []).forEach((item) => {
      if (!item?.orderDate || !item?.orderAmount) return;

      const date = new Date(item.orderDate);
      const monthKey = date.toLocaleString("default", { month: "short" });
      const amount = parseFloat(item.orderAmount) || 0;

      if (!monthlyTotals[monthKey]) {
        monthlyTotals[monthKey] = 0;
      }
      monthlyTotals[monthKey] += amount;
    });

    return Array.from({ length: 12 }, (_, i) => {
      const month = new Date(0, i).toLocaleString("default", { month: "short" });
      return { name: month, value: monthlyTotals[month] || 0 };
    });
  }, [revenueData]);

  // 🔹 Group into quarters
  // 🔹 Group into financial year quarters (Apr–Mar)
  const quarterlyData = useMemo(() => {
    // Reorder months to start from April
    const reorderedMonths = [
      ...monthlyData.slice(3), // Apr–Dec
      ...monthlyData.slice(0, 3), // Jan–Mar
    ];

    return [
      { name: "Q1", value: reorderedMonths.slice(0, 3).reduce((a, m) => a + m.value, 0) },  // Apr–Jun
      { name: "Q2", value: reorderedMonths.slice(3, 6).reduce((a, m) => a + m.value, 0) },  // Jul–Sep
      { name: "Q3", value: reorderedMonths.slice(6, 9).reduce((a, m) => a + m.value, 0) },  // Oct–Dec
      { name: "Q4", value: reorderedMonths.slice(9, 12).reduce((a, m) => a + m.value, 0) }, // Jan–Mar
    ];
  }, [monthlyData]);




  const [selectedPeriod, setSelectedPeriod] = useState("MONTH");
  const [chartType, setChartType] = useState("LINE");

  const chartData = selectedPeriod === "MONTH" ? monthlyData : quarterlyData;
  const maxRevenue = Math.max(...chartData.map((d) => d.value), 0);

  const yAxisTickFormatter = (value) => {
    if (value === 0) return "";
    if (value >= 1_000_000) return `₹${(value / 1_000_000).toFixed(1)}m`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(0)}k`;
    return `₹${value}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div style={tooltipStyle}>
          <p style={{ margin: 0, color: "#333" }}>
            {t("tooltipLabel", {
              label,
              value: payload[0].value.toLocaleString(),
            })}
          </p>
        </div>
      );
    }
    return null;
  };

  const getFiscalYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const startYear = now.getMonth() < 3 ? year - 1 : year;
    const endYear = startYear + 1;
    return `${String(startYear).slice(2)}-${String(endYear).slice(2)}`;
  };

  const periods = [
    { key: "MONTH", label: t("month") },
    { key: "QUARTER", label: t("quarter") },
  ];

  const chartTypes = [
    { key: "LINE", label: t("lineChart") },
    { key: "BAR", label: t("barChart") },
  ];

  const tooltipStyle = {
    backgroundColor: "#fff",
    border: "1px solid #d9d9d9",
    borderRadius: "5px",
    padding: "8px",
    fontSize: "12px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
  };

  if (loading) return <p>Loading revenue data...</p>;

  return (
    <div
      style={{
        padding: "30px",
        marginTop: "44px",
        backgroundColor: "#fff",
        borderRadius: "10px",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        width: "95%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "20px",
          gap: "10px",
        }}
      >
        <h3 style={{ fontSize: "24px", fontWeight: "bold", color: "#212529" }}>
          {t("revenueFY", { year: getFiscalYear() })}
        </h3>


        <div style={{ display: "flex", gap: "10px" }}>
          {periods.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSelectedPeriod(key)}
              style={{
                padding: "8px 15px",
                borderRadius: "8px",
                border: "none",
                fontSize: "14px",
                fontWeight: "bold",
                backgroundColor: selectedPeriod === key ? "#212529" : "#f0f0f0",
                color: selectedPeriod === key ? "#fff" : "#606060",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={250}>
        {chartType === "LINE" ? (
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 50, bottom: 30 }}
          >
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#606060" }} />
            <YAxis
              width={60}
              tick={{ fontSize: 12, fill: "#606060" }}
              tickFormatter={yAxisTickFormatter}
              domain={[0, Math.ceil(maxRevenue / 1000) * 1000]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 4, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }}
              activeDot={{ r: 6, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }}
            >
              <LabelList
                dataKey="value"
                content={({ x, y, value, index }) =>
                  index !== 0 ? (
                    <text
                      x={x}
                      y={y - 10}
                      fill="#212529"
                      fontSize={11}
                      textAnchor="middle"
                    >
                      ₹{value.toLocaleString()}
                    </text>
                  ) : null
                }
              />
            </Line>
          </LineChart>
        ) : (
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 50, bottom: 30 }}
          >
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#606060" }} />
            <YAxis
              width={60}
              tick={{ fontSize: 12, fill: "#606060" }}
              tickFormatter={yAxisTickFormatter}
              domain={[0, Math.ceil(maxRevenue / 1000) * 1000]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" fill="#3b82f6" radius={[6, 6, 0, 0]}>
              <LabelList
                dataKey="value"
                position="top"
                formatter={(value) => `₹${value.toLocaleString()}`}
              />
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>

      <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
        {chartTypes.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setChartType(key)}
            style={{
              padding: "6px 14px",
              borderRadius: "8px",
              border: "none",
              fontSize: "13px",
              fontWeight: "bold",
              backgroundColor: chartType === key ? "#212529" : "#f0f0f0",
              color: chartType === key ? "#fff" : "#212529",
              cursor: "pointer",
              transition: "all 0.2s",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default BusinessInsightsChart;
